import {
	DefaultButtonCompat as DefaultButton,
	DropdownCompat as Dropdown,
	IconCompat as Icon,
	PivotCompat as Pivot,
	PivotItem,
	SpinnerCompat as Spinner,
	StackCompat as Stack,
	TextCompat as Text,
} from 'components/FluentUICompat';
import React, {FormEvent, useCallback, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {
	createSearchParams,
	useNavigate,
	useSearchParams,
} from 'react-router-dom';
import {useState} from 'react';
import {checkSearchValid} from 'components/TextSearchBar';
import {useCommand} from 'hooks';
import {v4 as uuidv4} from 'uuid';

import {useTopStickySectionContext} from 'components/TopStickyComponent/TopStickySectionContext';
import {getPivotIcon, getSearchPivotItem} from './SearchPivotitem';
import {SearchState} from 'features/Search/SearchPage';
import {
	DeepSearchParagraphsQuery,
	FullTextSearchPogisQuery,
	FullTextSearchRegulationsQuery,
	FullTextSearchRegulatoryDocumentsQuery,
	FullTextSearchRequirementsQuery,
} from 'features/Search/hooks';

type EntityType =
	| 'regulations'
	| 'regulatorydocuments'
	| 'requirements'
	| 'pogisdocuments'
	| 'paragraphs';

type SearchPivotProps = {
	searchText: string;
	currentViewKey: string;
	currentTop: number;
	isResultCardFormat: boolean;
	requirementsLoading: boolean;
	regulationsLoading: boolean;
	documentsLoading: boolean;
	pogisLoading: boolean;
	deepSearchLoading: boolean;
	currentSearchState: SearchState;
	setCurrentViewKey: (searchText: string) => void;
	requirementData: FullTextSearchRequirementsQuery | undefined;
	regulationData: FullTextSearchRegulationsQuery | undefined;
	documentData: FullTextSearchRegulatoryDocumentsQuery | undefined;
	pogisData: FullTextSearchPogisQuery | undefined;
	deepSearchData: DeepSearchParagraphsQuery | undefined;
};

export const SearchPivot: React.FC<SearchPivotProps> = ({
	searchText,
	currentViewKey,
	currentTop,
	isResultCardFormat,
	setCurrentViewKey,
	currentSearchState,
	requirementsLoading,
	regulationsLoading,
	documentsLoading,
	pogisLoading,
	deepSearchLoading,
	requirementData,
	regulationData,
	documentData,
	pogisData,
	deepSearchData,
}) => {
	const {t} = useTranslation('features/search');
	const {setStickyChildren} = useTopStickySectionContext();
	const navigate = useNavigate();

	const onPivotLinkClick = useCallback(
		(item: any | undefined) => {
			if (item?.props.itemKey) {
				navigate({
					pathname: '',
					search: createSearchParams({
						entity: item.props.itemKey as EntityType,
						text: searchText,
					}).toString(),
				});
				setCurrentViewKey(item.props.itemKey);
			}
		},
		[searchText],
	);

	const pivotItemAll = useMemo(
		() =>
			isResultCardFormat && (
				<PivotItem
					headerText={t('All')}
					itemKey='all'
					itemIcon={getPivotIcon(currentViewKey, 'all')}
				/>
			),
		[currentViewKey, isResultCardFormat],
	);

	const propsPivotItemBasic = useMemo(
		() => ({
			t,
			searchText,
			currentViewKey,
			currentTop,
		}),
		[searchText, currentViewKey, currentTop],
	);

	const propsPivotReq = useMemo(
		() => ({
			entityName: 'Requirements',
			loading: requirementsLoading,
			searchStateEntity: currentSearchState.requirements,
			items: requirementData?.searchRequirements.requirements,
			...propsPivotItemBasic,
		}),
		[
			currentSearchState,
			requirementsLoading,
			requirementData,
			propsPivotItemBasic,
		],
	);
	const propsPivotRegulation = useMemo(
		() => ({
			entityName: 'Regulations',
			loading: regulationsLoading,
			searchStateEntity: currentSearchState.regulations,
			items: regulationData?.searchRegulations.regulations,
			...propsPivotItemBasic,
		}),
		[
			currentSearchState,
			regulationsLoading,
			regulationData,
			propsPivotItemBasic,
		],
	);

	const propsPivotRegDoc = useMemo(
		() => ({
			entityName: 'RegulatoryDocuments',
			loading: documentsLoading,
			searchStateEntity: currentSearchState.regulatorydocuments,
			items: documentData?.searchRegulatoryDocuments.regulatoryDocuments,
			...propsPivotItemBasic,
		}),
		[currentSearchState, documentsLoading, documentData, propsPivotItemBasic],
	);

	const propsPivotPOGIS = useMemo(
		() => ({
			entityName: 'PogisDocuments',
			loading: pogisLoading,
			searchStateEntity: currentSearchState.pogisdocuments,
			items: pogisData?.searchPogisDocuments.pogisDocuments,
			...propsPivotItemBasic,
		}),
		[currentSearchState, pogisLoading, pogisData, propsPivotItemBasic],
	);

	const propsPivotDeepSearch = useMemo(
		() => ({
			entityName: 'Paragraphs',
			loading: deepSearchLoading,
			searchStateEntity: currentSearchState.paragraphs,
			items: deepSearchData?.searchParagraphs.regulatoryDocumentsOtherData,
			...propsPivotItemBasic,
		}),
		[
			currentSearchState,
			deepSearchLoading,
			deepSearchData,
			propsPivotItemBasic,
		],
	);

	const stickyObject = useMemo(
		() => (
			<Stack horizontal style={{height: 50}}>
				<Pivot
					onLinkClick={onPivotLinkClick}
					selectedKey={currentViewKey}
					style={{
						margin: 'auto',
					}}
				>
					{pivotItemAll}
					{getSearchPivotItem(propsPivotReq)}
					{getSearchPivotItem(propsPivotRegulation)}
					{getSearchPivotItem(propsPivotRegDoc)}
					{getSearchPivotItem(propsPivotPOGIS)}
					{getSearchPivotItem(propsPivotDeepSearch)}
				</Pivot>
			</Stack>
		),
		[
			pivotItemAll,
			propsPivotReq,
			propsPivotRegulation,
			propsPivotRegDoc,
			propsPivotPOGIS,
			propsPivotDeepSearch,
		],
	);

	useEffect(() => {
		const stickyChild = {
			key: 'pivot',
			sortPriority: 10,
			height: 50,
			isHidden: false,
			stickyObject,
		};
		setStickyChildren(prev => [
			...prev.filter(x => x.key !== stickyChild.key),
			stickyChild,
		]);
	}, [stickyObject, setStickyChildren]);

	return <></>;
};
